import { AssertionException } from '../../../../../utils/log';
import { PreviouslySelectedOptionsForWidgets } from '../../../../reducers/student/tracking/questionWidgetTrackingReducer';
import { SemicolonSerializer } from '../serializer/Serializer';
import {
  CourseCompletionStatus,
  IPersistenceTrackingClient,
  PersistenceTrackingData,
  TrackingDataVersion,
} from '../trackingClient';
import type { ApiClient, TrackingIdentifier } from '@ael/core-api';
import { Map, Set } from 'immutable';

type ServerLogData = {
  log: string;
  time: number;
};

export class ServerClient implements IPersistenceTrackingClient {
  private timeLastUpdated: number;
  private progressTrackingId: number;
  private timeTaken: number;
  private completedAt: number | null;
  private projectGuid: string;
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient, projectGuid: string, currentDateNow: number = Date.now()) {
    this.timeLastUpdated = currentDateNow;
    this.projectGuid = projectGuid;
    this.apiClient = apiClient;
  }

  name = 'v360eStudentOnlineDbTracking';
  doesSupportLegacyData = true;

  async init(): Promise<boolean> {
    const response = await this.apiClient['/api/progress-tracking/initialize/{courseGuid}'].post({
      params: { courseGuid: this.projectGuid },
    });
    const initResult = await response.json();
    if (initResult === null) {
      throw AssertionException(`Progress tracking does not exist for a project with guid: ${this.projectGuid}`);
    }

    // Javascript can take any C# long if it is unix timestamp represented in seconds, which this is
    const completedAtUnixTimestampSeconds = initResult.completedAt ? initResult.completedAt as number : null;
    this.progressTrackingId = initResult.id;
    this.timeTaken = initResult.timeTaken;
    this.completedAt = completedAtUnixTimestampSeconds;
    return true;
  }

  async getTrackingData(): Promise<PersistenceTrackingData> {
    const response = await this.apiClient['/api/progress-tracking/{progressTrackingId}'].get({
      params: { progressTrackingId: this.progressTrackingId },
    });
    const progress = await response.json()
    if (progress?.data === null) {
      return {
        finishedItems: Set<TrackingIdentifier>(),
        widgetTrackingData: Map<TrackingIdentifier, PreviouslySelectedOptionsForWidgets>(),
        version: TrackingDataVersion.v2,
      };
    }
    return SemicolonSerializer.deserializeData(progress.data);
  }

  async setTrackingData({ widgetTrackingData, finishedItems }: PersistenceTrackingData): Promise<void> {
    this.timeTaken = this._getTimeStudentSpentInCourse();
    const payload: ServerLogData = {
      log: SemicolonSerializer.serializeData(finishedItems, widgetTrackingData),
      time: this.timeTaken,
    };
    await this.apiClient['/api/progress-tracking/{progressTrackingId}'].post({
      params: { progressTrackingId: this.progressTrackingId},
      json: payload
    })
    this.timeLastUpdated = Date.now();
  }

  async setCompletionStatus(status: CourseCompletionStatus): Promise<void> {
    if (this.completedAt === null && status === CourseCompletionStatus.Completed) {
      this.completedAt = Date.now();
      await this.apiClient['/api/progress-tracking/SetCompleted/{progressTrackingId}'].post({
        params: { progressTrackingId: this.progressTrackingId},
        json: status
      })
    }
  }

  async setQuizStatus() {
    return Promise.resolve();
  }

  _getTimeStudentSpentInCourse(): number {
    return Date.now() - this.timeLastUpdated + this.timeTaken;
  }

  afterSetup(): Promise<void> {
    return Promise.resolve(undefined);
  }
}
